/* eslint-disable babel/camelcase */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { METADATA_COLLAPSED_HEIGHT } from './constants'
import {
  join,
  some,
  startCase,
} from 'lodash-es'
import { memo, useMemo } from 'react'
import FaIcon from 'd2/components/FaIcon'
import HyperLink from 'd2/components/Typography/HyperLink'
import MetadataBox from 'd2/components/MetadataBox'
import MetadataBoxRow from 'd2/components/MetadataBoxRow'
import ShowMore from 'd2/components/ShowMore'
import useStyle from './styles'
import useTranslations from './translations'
import type { Props } from './types'

const CollapsibleAlbumMetadata = memo<Props>(({
  albumName,
  artistName,
  collaboratingArtists,
  copyrightName,
  copyrightYear,
  genre,
  label,
  partners,
  territories,
  upc,
}) => {
  const t = useTranslations()
  const { classes } = useStyle()

  const collapsedText = useMemo(() => (<HyperLink>
    { t.collapsedText }
    <FaIcon
      className={classes.chevron}
      icon='chevron-down'
      weight='light'
    />
  </HyperLink>), [classes.chevron, t.collapsedText])

  const expandedText = useMemo(() => (<HyperLink>
    { t.expandedText }
    <FaIcon
      className={classes.chevron}
      icon='chevron-up'
      weight='light'
    />
  </HyperLink>), [classes.chevron, t.expandedText])

  return (
    <ShowMore
      align='center'
      className={classes.showMore}
      collapsedHeight={METADATA_COLLAPSED_HEIGHT}
      collapsedText={collapsedText}
      expandedText={expandedText}
    >
      <MetadataBox>
        <MetadataBoxRow
          className={classes.infoRow}
          label={t.labelAlbumName}
          noTruncate
        >
          { albumName || t.noData }
        </MetadataBoxRow>
        <MetadataBoxRow
          className={classes.infoRow}
          label={t.labelArtistName}
          noTruncate
        >
          { artistName || t.noData }
        </MetadataBoxRow>
        { collaboratingArtists?.map(({ id, name, role }) => (
          <MetadataBoxRow
            className={classes.infoRow}
            key={id}
            label={startCase(role)}
            noTruncate
          >
            { name || t.noData }
          </MetadataBoxRow>
        )) }
        <MetadataBoxRow
          className={classes.infoRow}
          label={t.labelGenre}
          noTruncate
        >
          { genre || t.noData }
        </MetadataBoxRow>
        <MetadataBoxRow
          className={classes.infoRow}
          label={t.labelLabel}
          noTruncate
        >
          { label || t.noData }
        </MetadataBoxRow>
        <MetadataBoxRow
          className={classes.infoRow}
          label={t.labelCopyrightName}
          noTruncate
        >
          { copyrightName || t.noData }
        </MetadataBoxRow>
        <MetadataBoxRow
          className={classes.infoRow}
          label={t.labelCopyrightYear}
          noTruncate
        >
          { copyrightYear || t.noData }
        </MetadataBoxRow>
        <MetadataBoxRow
          className={classes.infoRow}
          label={t.labelUPC}
          noTruncate
        >
          { upc || t.noData }
        </MetadataBoxRow>
        { partners?.map(({ id, network, partner_eid, partner_url }) => (
          <MetadataBoxRow
            className={classes.infoRow}
            key={id}
            label={network.name}
            noTruncate
          >
            { /* TODO: Consider click-to-copy instead */ }
            <HyperLink
              href={partner_url}
              target='_blank'
            >
              { partner_eid }
            </HyperLink>
          </MetadataBoxRow>
        )) }
        <MetadataBoxRow
          className={classes.infoRow}
          label={t.labelTerritories}
        >
          { some(territories) ? join(territories, ', ') : t.noData }
        </MetadataBoxRow>
      </MetadataBox>
    </ShowMore>
  )
})

CollapsibleAlbumMetadata.displayName = 'CollapsibleAlbumMetadata'

export default CollapsibleAlbumMetadata
